import BaseRender from "./baserender.js";
import {mat4,vec3}  from 'gl-matrix'
import { PixelType, ContentFitType } from "../../constant/index.js";
import { logger } from "../../utils/logger.js";
import { createTexture, initShaderProgram } from "./webglutil";


const vsSource = `
attribute vec4 aVertexPosition;
attribute vec2 aTexturePosition;
varying lowp vec2 vTexturePosition;
void main(void) {
  gl_Position =  aVertexPosition;
  vTexturePosition = aTexturePosition;
}
`;

// Fragment shader program

const fsSource = `
precision mediump float;
varying mediump vec2 vTexturePosition;
uniform int pixelType;
uniform sampler2D rgbaTexture; 
uniform sampler2D yTexture; 
uniform sampler2D uTexture; 
uniform sampler2D vTexture; 
uniform sampler2D uvTexture; 
uniform mat4 colorMatrix;

const mat4 YUV2RGB = mat4( 1.1643828125, 0, 1.59602734375, -.87078515625,
                           1.1643828125, -.39176171875, -.81296875, .52959375,
                           1.1643828125, 2.017234375, 0, -1.081390625,
                           0, 0, 0, 1);

vec4 getPixel(vec2 texpos) {

  vec4 pixel;
  if (pixelType == 1) {

      float y = texture2D(yTexture,  texpos).r;
      float u = texture2D(uTexture,  texpos).r;
      float v = texture2D(vTexture,  texpos).r;
      pixel = vec4(y, u, v, 1) * YUV2RGB;

  } else if (pixelType == 2) {

      float y = texture2D(yTexture,  texpos).r;
      vec2 uv = texture2D(uvTexture,  texpos).ra;
      pixel = vec4(y, uv.x, uv.y, 1) * YUV2RGB;

  } else {

      pixel =  texture2D(rgbaTexture, texpos);
  }

  return colorMatrix*pixel;
}


void main(void) {

  gl_FragColor = getPixel(vTexturePosition);
}
`;


class RectRender extends BaseRender {

  
    pixeltype = PixelType.YUV_I420;

    textureWidth = 0;
    textureHeight = 0;
    programInfo = undefined;
    buffers = undefined;

    textureHasLoad = false;


    tag = 'RectRender'

    constructor(gl, width, height, renderOptions) {

      super(width, height, renderOptions);

      this.gl = gl

      this.gl.pixelStorei(this.gl.UNPACK_ALIGNMENT, 1);
      const shaderProgram = initShaderProgram(this.gl, vsSource, fsSource);
    
      this.programInfo  = {
        program: shaderProgram,
        attribLocations: {
          vertexPosition: this.gl.getAttribLocation(shaderProgram, 'aVertexPosition'),
          texturePosition: this.gl.getAttribLocation(shaderProgram, 'aTexturePosition'),
        },
        uniformLocations: {
          rgbatexture: this.gl.getUniformLocation(shaderProgram, 'rgbaTexture'),
          ytexture: this.gl.getUniformLocation(shaderProgram, 'yTexture'),
          utexture: this.gl.getUniformLocation(shaderProgram, 'uTexture'),
          vtexture: this.gl.getUniformLocation(shaderProgram, 'vTexture'),
          uvtexture: this.gl.getUniformLocation(shaderProgram, 'uvTexture'),
          pixelType: this.gl.getUniformLocation(shaderProgram, 'pixelType'),
          colorMatrix: this.gl.getUniformLocation(shaderProgram, 'colorMatrix'),
        }
      };
    
      this.buffers = this.initBuffers();
      this.rgbatexture = createTexture(this.gl);
      this.ytexture = createTexture(this.gl);
      this.utexture = createTexture(this.gl);
      this.vtexture = createTexture(this.gl);
      this.uvtexture = createTexture(this.gl);

    }


    initBuffers() {

      const positionBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, positionBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([-1.0, -1.0, -1.0, 1.0, -1.0, -1.0, 1.0, 1.0, -1.0, -1.0, 1.0, -1.0]), this.gl.STATIC_DRAW);

      const texpositionBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ARRAY_BUFFER, texpositionBuffer);
      this.gl.bufferData(this.gl.ARRAY_BUFFER, new Float32Array([0.0, 1.0, 1.0, 1.0, 1.0, 0.0, 0.0, 0.0]), this.gl.STATIC_DRAW);

      const indexBuffer = this.gl.createBuffer();
      this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, indexBuffer);
      this.gl.bufferData(this.gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0,  1,  2,  0,  2,  3]), this.gl.STATIC_DRAW);

      return {
        position: positionBuffer,
        texposition: texpositionBuffer,
        indices: indexBuffer
      };
    }

    destroy() {

        this.gl.deleteProgram(this.programInfo.program);

        this.gl.deleteBuffer(this.buffers.position);
        this.gl.deleteBuffer(this.buffers.texposition);
        this.gl.deleteBuffer(this.buffers.indices);

        this.gl.deleteTexture(this.rgbatexture);
        this.gl.deleteTexture(this.ytexture);
        this.gl.deleteTexture(this.utexture);
        this.gl.deleteTexture(this.vtexture);
        this.gl.deleteTexture(this.uvtexture);

        super.destroy();

         logger.info(this.tag, 'RectRender destroy'); 
    }

    updateTexture(pixeltype, pixelbuf, width, height) {

      this.pixeltype = pixeltype;
      this.textureWidth = width;
      this.textureHeight = height;

      if (pixeltype === PixelType.DOM) {

          let textunit = 3;
          this.gl.activeTexture(this.gl.TEXTURE0 + textunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.rgbatexture);

          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, this.gl.RGBA,  this.gl.UNSIGNED_BYTE, pixelbuf);
  

      }else if (pixeltype === PixelType.RGBA) {

          let textunit = 3;
          this.gl.activeTexture(this.gl.TEXTURE0 + textunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.rgbatexture);
  
          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, width, height, 0, this.gl.RGBA, this.gl.UNSIGNED_BYTE, pixelbuf);

      } else if (pixeltype === PixelType.YUV_I420) {

          let y = pixelbuf.slice(0, width*height);
          let u = pixelbuf.slice(width*height, width*height*5/4);
          let v = pixelbuf.slice(width*height*5/4, width*height*3/2);

          this.gl.activeTexture(this.gl.TEXTURE0);
          this.gl.bindTexture(this.gl.TEXTURE_2D,  this.ytexture);
          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.LUMINANCE, width, height, 0, this.gl.LUMINANCE, this.gl.UNSIGNED_BYTE, y);
          this.gl.activeTexture(this.gl.TEXTURE1);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.utexture);
          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.LUMINANCE, width/2, height/2, 0, this.gl.LUMINANCE, this.gl.UNSIGNED_BYTE, u);
          this.gl.activeTexture(this.gl.TEXTURE2);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.vtexture);
          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.LUMINANCE, width/2, height/2, 0, this.gl.LUMINANCE, this.gl.UNSIGNED_BYTE, v);

      }  else if (pixeltype === PixelType.YUV_NV12) {

          let y = pixelbuf.slice(0, width*height);
          let uv = pixelbuf.slice(width*height, width*height*3/2);
  
          this.gl.activeTexture(this.gl.TEXTURE0);
          this.gl.bindTexture(this.gl.TEXTURE_2D,  this.ytexture);
          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.LUMINANCE, width, height, 0, this.gl.LUMINANCE, this.gl.UNSIGNED_BYTE, y);
          this.gl.activeTexture(this.gl.TEXTURE1);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.uvtexture);
          this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.LUMINANCE_ALPHA, width/2, height/2, 0, this.gl.LUMINANCE_ALPHA, this.gl.UNSIGNED_BYTE, uv);


      }else {

          return;
      }

      if (!this.textureHasLoad) {

        this.textureHasLoad = true
      }

      this.drawScene()

  }

    doResizeView() {

      this.drawScene()
      
    }

    doUpdateRenderOptions() {

      this.drawScene()
    }


    drawScene() {

      if (!this.textureHasLoad) {

        return
    }

        if (this.textureWidth === 0 || this.textureHeight === 0) {

            this.gl.viewport(0 , 0, this.width, this.height);

        } else {

          let textureAspectRatio = this.textureWidth/this.textureHeight
          let aspectRatio = this.width/this.height


          if (this.renderOptions.contentFit == ContentFitType.contain) {

            if (textureAspectRatio > aspectRatio) {

              let adjustHeight = this.width/textureAspectRatio
              this.gl.viewport(0 , (this.height- adjustHeight)/2, this.width, adjustHeight);

            } else {

              let adjustWidth = this.height*textureAspectRatio
              this.gl.viewport((this.width - adjustWidth)/2 , 0, adjustWidth, this.height);
            }

          } else if (this.renderOptions.contentFit == ContentFitType.cover) {

            if (textureAspectRatio > aspectRatio) {

                let adjustWidth = this.height*textureAspectRatio
                this.gl.viewport((this.width - adjustWidth)/2 , 0, adjustWidth, this.height);

            } else {

                let adjustHeight = this.width/textureAspectRatio
                this.gl.viewport(0 , (this.height- adjustHeight)/2, this.width, adjustHeight);
            }

          } else {

            this.gl.viewport(0 , 0, this.width, this.height);

          }

        }

        this.gl.clearColor(0.0, 0.0, 0.0, 0.0);  
        this.gl.clearDepth(1.0);                 
        this.gl.enable(this.gl.DEPTH_TEST);        
        this.gl.depthFunc(this.gl.LEQUAL);            
        this.gl.clear(this.gl.COLOR_BUFFER_BIT | this.gl.DEPTH_BUFFER_BIT);
      

      
        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.position);
        this.gl.vertexAttribPointer(this.programInfo.attribLocations.vertexPosition, 3, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(this.programInfo.attribLocations.vertexPosition);

        this.gl.bindBuffer(this.gl.ARRAY_BUFFER, this.buffers.texposition);
        this.gl.vertexAttribPointer(this.programInfo.attribLocations.texturePosition, 2, this.gl.FLOAT, false, 0, 0);
        this.gl.enableVertexAttribArray(this.programInfo.attribLocations.texturePosition);

        let rgbatextunit = 2;
        let ytextunit = rgbatextunit + 1;
        let utextunit = rgbatextunit + 2;
        let vtextunit = rgbatextunit + 3;
        let uvtextunit = rgbatextunit + 4;

        if (this.pixeltype === PixelType.YUV_I420) {

          this.gl.activeTexture(this.gl.TEXTURE0 + ytextunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.ytexture);
          this.gl.activeTexture(this.gl.TEXTURE0 + utextunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.utexture);
          this.gl.activeTexture(this.gl.TEXTURE0 + vtextunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.vtexture);

      }  else if (this.pixeltype === PixelType.YUV_NV12) {

          this.gl.activeTexture(this.gl.TEXTURE0 + ytextunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.ytexture);
          this.gl.activeTexture(this.gl.TEXTURE0 + uvtextunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.uvtexture);

      } else {

          this.gl.activeTexture(this.gl.TEXTURE0 + rgbatextunit);
          this.gl.bindTexture(this.gl.TEXTURE_2D, this.rgbatexture);

      }

        this.gl.bindBuffer(this.gl.ELEMENT_ARRAY_BUFFER, this.buffers.indices);
      
        this.gl.useProgram(this.programInfo.program);

        this.gl.uniform1i(this.programInfo.uniformLocations.rgbatexture, rgbatextunit);
        this.gl.uniform1i(this.programInfo.uniformLocations.ytexture, ytextunit);
        this.gl.uniform1i(this.programInfo.uniformLocations.utexture, utextunit);
        this.gl.uniform1i(this.programInfo.uniformLocations.vtexture, vtextunit);
        this.gl.uniform1i(this.programInfo.uniformLocations.uvtexture, uvtextunit);
        this.gl.uniform1i(this.programInfo.uniformLocations.pixelType, this.pixeltype);
        this.gl.uniformMatrix4fv(this.programInfo.uniformLocations.colorMatrix, false, this.colorMatrix);

        this.gl.drawElements(this.gl.TRIANGLES, 6, this.gl.UNSIGNED_SHORT, 0);

      }

}


export default RectRender;